import { CloudUploadOutlined, DeleteOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Space, Tag, Tooltip } from 'antd';
import { formatShanghaiDateTime } from '../../utils/datetime';
import { fixMojibakeUtf8 } from '../../utils/text';
import { isVideoType, normalizeContentType } from './helpers';

const SUMMARY_TOOLTIP_WIDTH = 800;
const SUMMARY_TOOLTIP_MAX_WIDTH = 'min(800px, calc(100vw - 24px))';
const SUMMARY_TOOLTIP_MAX_HEIGHT = '60vh';

const formatDateTime = (dateTimeString) => formatShanghaiDateTime(dateTimeString);

const getStatusTag = (status) => {
  const statusConfig = {
    已发布: { color: 'success', text: '已发布' },
    待发布: { color: 'warning', text: '待发布' },
    发布中: { color: 'processing', text: '发布中' },
    草稿: { color: 'warning', text: '草稿' },
    已下线: { color: 'error', text: '已下线' },
    作废: { color: 'error', text: '作废' },
  };
  const config = statusConfig[status] || { color: 'default', text: status };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const renderWrapText = (value) => (
  <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
    {value ? String(value) : '-'}
  </div>
);

const renderSummaryWithTooltip = (value) => {
  const text = value ? String(value) : '';
  if (!text) return '-';
  const needsTruncate = text.length > 100;
  const display = needsTruncate ? `${text.slice(0, 100)}…` : text;
  const content = (
    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {display}
    </div>
  );
  if (!needsTruncate) return content;
  return (
    <Tooltip
      title={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>}
      placement="top"
      arrow={{ pointAtCenter: true }}
      autoAdjustOverflow={{ adjustX: 1, adjustY: 1 }}
      styles={{
        container: {
          width: SUMMARY_TOOLTIP_WIDTH,
          maxWidth: SUMMARY_TOOLTIP_MAX_WIDTH,
          maxHeight: SUMMARY_TOOLTIP_MAX_HEIGHT,
          overflowY: 'auto',
        },
      }}
      overlayInnerStyle={{
        width: SUMMARY_TOOLTIP_WIDTH,
        maxWidth: SUMMARY_TOOLTIP_MAX_WIDTH,
        maxHeight: SUMMARY_TOOLTIP_MAX_HEIGHT,
        overflowY: 'auto',
      }}
    >
      {content}
    </Tooltip>
  );
};

export const buildContentListColumns = ({
  handleContentIdClick,
  handlePublish,
  handleDownload,
  handleDelete,
  publishEntryBlocked,
  noActivePlan,
}) => [
  {
    title: '内容id',
    dataIndex: 'content_id',
    key: 'content_id',
    width: 120,
    render: (text) => (
      <Button
        type="link"
        size="small"
        onClick={() => handleContentIdClick(text, 'preview')}
        block
        style={{
          padding: 0,
          height: 'auto',
          textAlign: 'left',
          whiteSpace: 'normal',
          wordBreak: 'break-all',
        }}
      >
        {text}
      </Button>
    ),
  },
  {
    title: '内容来源',
    dataIndex: 'source',
    key: 'source',
    width: 120,
  },
  {
    title: '内容类型',
    dataIndex: 'content_type',
    key: 'content_type',
    width: 120,
    render: (contentType) => {
      const mapped = normalizeContentType(contentType);
      if (!mapped) return '-';
      return <span style={{ color: '#333' }}>{mapped}</span>;
    },
  },
  {
    title: '创作者',
    dataIndex: 'author',
    key: 'author',
    width: 140,
    render: (value, record) => {
      const authorText = value ? String(fixMojibakeUtf8(value)).trim() : '';
      const creatorId = record?.creator_user_id ? String(record.creator_user_id).trim() : '';
      const display = authorText || creatorId || '-';
      if (display === '-') return '-';
      const title = authorText && creatorId ? `${authorText}\n${creatorId}` : display;
      return <span title={title}>{display}</span>;
    },
  },
  {
    title: '创作时间',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 180,
    render: (text) => formatDateTime(text),
    sorter: (a, b) => {
      if (!a.created_at || !b.created_at) return 0;
      return new Date(a.created_at) - new Date(b.created_at);
    },
  },
  {
    title: '入库时间',
    dataIndex: 'ingested_at',
    key: 'ingested_at',
    width: 180,
    render: (text) => formatDateTime(text),
    sorter: (a, b) => {
      if (!a.ingested_at || !b.ingested_at) return 0;
      return new Date(a.ingested_at) - new Date(b.ingested_at);
    },
  },
  {
    title: '标题',
    dataIndex: 'title',
    key: 'title',
    width: 200,
    render: renderWrapText,
  },
  {
    title: '内容简介',
    dataIndex: 'summary',
    key: 'summary',
    width: 240,
    render: renderSummaryWithTooltip,
  },
  {
    title: '一级分类',
    dataIndex: 'category_level1',
    key: 'category_level1',
    width: 120,
  },
  {
    title: '二级分类',
    dataIndex: 'category_level2',
    key: 'category_level2',
    width: 120,
  },
  {
    title: '内容标签',
    dataIndex: 'tags',
    key: 'tags',
    width: 150,
    render: (tags) => {
      if (Array.isArray(tags)) {
        return tags.map((tag, index) => <Tag key={index}>{tag}</Tag>);
      }
      return tags;
    },
  },
  {
    title: '风险状态',
    dataIndex: 'risk_flag',
    key: 'risk_flag',
    width: 100,
    render: (value) => (value ? '高风险' : '低风险'),
  },
  {
    title: '是否需要人工审核',
    dataIndex: 'manual_review_required',
    key: 'manual_review_required',
    width: 100,
    render: (value) => (value ? '是' : '否'),
  },
  {
    title: '是否审核',
    dataIndex: 'is_manual_reviewed',
    key: 'is_manual_reviewed',
    width: 100,
    render: (value) => (value ? '是' : '否'),
  },
  {
    title: '内容状态',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (status) => getStatusTag(status),
  },
  {
    title: '投递平台',
    dataIndex: 'available_platforms',
    key: 'available_platforms',
    width: 200,
    render: (platforms) => {
      if (Array.isArray(platforms)) {
        return platforms.join(', ');
      }
      return platforms;
    },
  },
  {
    title: '操作',
    key: 'action',
    width: 300,
    fixed: 'right',
    render: (_, record) => (
      <Space size={[8, 0]} wrap>
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleContentIdClick(record.content_id, 'edit')}
          style={{ padding: '0 4px' }}
        >
          编辑
        </Button>
        <Tooltip
          title={publishEntryBlocked ? (
            <span>
              {noActivePlan ? '当前订阅未生效，请先购买或续费。' : '月发布与任务额度均已用完，升级套餐后可继续发布。'}
              <a href="/billing" style={{ marginLeft: 6 }}>去付费订阅</a>
            </span>
          ) : null}
        >
          <span>
            <Button
              type="link"
              size="small"
              icon={<CloudUploadOutlined />}
              onClick={() => handlePublish(record)}
              style={{ padding: '0 4px' }}
              disabled={publishEntryBlocked}
            >
              发布
            </Button>
          </span>
        </Tooltip>
        {isVideoType(record.content_type) && (
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
            style={{ padding: '0 4px' }}
          >
            下载
          </Button>
        )}
        <Button
          type="link"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.content_id)}
          style={{ padding: '0 4px' }}
        >
          删除
        </Button>
      </Space>
    ),
  },
];
